import {
    CreateAgencyRequest,
    AgencyState,
    AgencyObject,
    QuoteStatus,
    PolicyStatus,
    Flag,
    AddLocalTravelDataRequest,
    CancelTravelScheduleRequest
} from 'Application/models';
import {
    addAgencyLocalTravelData,
    cancelTravelSchedule,
    createAgencyCompany,
    EditAgencyCompany,
    fetchAgenciesNames,
    fetchAgencyStatistics,
    fetchIntermediaryType,
    fetchSingleAgency,
    fetchUnsupervisedAgenciesNames,
    getAgencies,
    getAgencyBranches,
    getAgencyLocalTravelData,
    getAgencyPolicies,
    getAgencyQuotations,
    getBritamInternalUsersQuotations,
    getFilteredAgencyLocalTravelData,
    getQuotationsReportData,
    updateAgencyLocalTravelData
} from 'Application/service';
import { create } from 'zustand';

export const useAgencyStore = create<AgencyState>((set, get) => ({
    isLoading: false,
    isError: false,
    error: null,
    isAgencyLoading: false,
    createAgencyData: {
        agency: {
            name: '',
            address: '',
            emailAddress: '',
            phoneNumber: '',
            agentNumber: '',
            intermediaryType: ''
        }
    } as CreateAgencyRequest,

    agencyData: null,
    agencyNameData: [],
    intermedialyTypeData: [],
    unsupervisedAgenciesData: [],

    //TODO: Review this - type in AgencyState is: SIngleAgency
    singleAgencyData: [],
    AgencyStatistics: null,
    agencyPoliciesData: null,
    agencyQuotationsData: null,
    quotationsReportData: null,
    agencyLocalTravelData: null,
    filteredAgencyLocalTravelData: null,
    agencyBranches: [],

    createAgency: async (values: CreateAgencyRequest) => {
        set({ isLoading: true });

        try {
            // Call API with FormData
            await createAgencyCompany(values);

            set({ isError: false, error: null });
            await get().getAgencies();
        } catch (error: any) {
            // Check if error has a response with a message
            const errorMessage = error?.response?.data?.message || 'Failed to create Agency';
            set({ error: errorMessage, isError: true });
            throw new Error(errorMessage); // Pass the detailed error message to the caller
        } finally {
            set({ isLoading: false });
        }
    },

    setAgencyDetailsStepOne: (data: Partial<AgencyObject>) => {
        set((state) => ({
            createAgencyData: {
                ...state.createAgencyData,
                agency: {
                    ...state.createAgencyData.agency,
                    ...data
                }
            }
        }));
    },

    setAgencyDocumentsStep: (registrationDoc: File[]) => {
        set((state) => ({
            createAgencyData: {
                ...state.createAgencyData,
                registrationDoc
            }
        }));
    },

    setAgencyCustomizationStep: (data: { agencyUrl: string; logo: File | null }) => {
        set((state) => ({
            createAgencyData: {
                ...state.createAgencyData,
                agency: {
                    ...state.createAgencyData.agency,
                    agencyUrl: data.agencyUrl
                },
                logo: data.logo
            }
        }));
    },

    getAgencies: async (region?: string | null, page?: number, pageSize?: number, searchValue?: string) => {
        set({ isLoading: true });

        try {
            const response = await getAgencies(region, page, pageSize, searchValue);
            console.log('Agency data response: ', response);

            set({ isError: false, error: null, agencyData: response });
        } catch (error) {
            set({ error: 'Failed to get agencies', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getAgenciesName: async () => {
        set({ isLoading: true });

        try {
            const response = await fetchAgenciesNames();

            set({ isError: false, error: null, agencyNameData: response });
        } catch (error) {
            set({ error: 'Failed to get agencies', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    getIntermedialyType: async (intermediaryType: string) => {
        set({ isLoading: true });

        try {
            const response = await fetchIntermediaryType(intermediaryType);

            set({ isError: false, error: null, intermedialyTypeData: response });
        } catch (error) {
            set({ error: 'Failed to get financial advisors', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    getUnsupervisedAgenciesName: async () => {
        set({ isLoading: true });

        try {
            const response = await fetchUnsupervisedAgenciesNames();

            set({ isError: false, error: null, unsupervisedAgenciesData: response });
        } catch (error) {
            set({ error: 'Failed to get agencies', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    //TODO: Review this - type in AgencyState is: SIngleAgency
    getSingleAgency: async (agencyId: any) => {
        set({ isAgencyLoading: true });

        try {
            const response = await fetchSingleAgency(agencyId);

            set({ isError: false, error: null, singleAgencyData: response });
        } catch (error) {
            set({ error: 'Failed to get agencies', isError: true });
            throw error;
        } finally {
            set({ isAgencyLoading: false });
        }
    },

    getAgencyStatistics: async (agencyName: string) => {
        set({ isLoading: true });

        try {
            const response = await fetchAgencyStatistics(agencyName);

            set({ isError: false, error: null, AgencyStatistics: response });
        } catch (error) {
            set({ error: 'Failed to get agencies', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getAgencyQuotations: async (agencyId: number, pageNumber: number, pageSize: number, status?: QuoteStatus, search?: string) => {
        set({ isLoading: true });

        try {
            const response = await getAgencyQuotations(agencyId, pageNumber, pageSize, status, search);

            set({
                isError: false,
                error: null,
                agencyQuotationsData: response
            });
        } catch (error) {
            set({ error: `Failed to get quotations by status for agent ${agencyId}`, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getAgencyPolicies: async (
        agencyName: string,
        pageNumber: number,
        pageSize: number,
        status?: PolicyStatus,
        policyNumber?: string,
        startDate?: string,
        endDate?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await getAgencyPolicies(agencyName, pageNumber, pageSize, status, policyNumber, startDate, endDate);

            set({
                isError: false,
                error: null,
                agencyPoliciesData: response
            });
        } catch (error) {
            set({ error: `Failed to get policies for agent ${agencyName}`, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    fetchBritamInternalUsersQuotations: async (
        pageNumber: number,
        pageSize: number,
        flag: Flag,
        status?: QuoteStatus,
        search?: string,
        agencyName?: string,
        region?: string,
        startDate?: string,
        endDate?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await getBritamInternalUsersQuotations(
                pageNumber,
                pageSize,
                flag,
                status,
                search,
                agencyName,
                region,
                startDate,
                endDate
            );

            set({
                isError: false,
                error: null,
                agencyQuotationsData: response
            });
        } catch (error) {
            set({ error: `Failed to get quotations by status`, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    editAgency: async (formData: FormData, agencyId: any) => {
        set({ isLoading: true });

        try {
            // Call EditAgencyCompany with formData and agencyId
            await EditAgencyCompany(formData, agencyId);

            set({ isError: false, error: null });
            await get().getSingleAgency(agencyId);
        } catch (error: any) {
            // Check if error has a response with a message
            const errorMessage = error?.response?.data?.message || 'Failed to edit Agency';
            set({ error: errorMessage, isError: true });
            throw new Error(errorMessage); // Pass the detailed error message to the caller
        } finally {
            set({ isLoading: false });
        }
    },

    getQuotationsReportData: async (
        search?: string,
        status?: QuoteStatus,
        agencyName?: string,
        regionName?: string,
        startDate?: string,
        endDate?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await getQuotationsReportData(search, status, agencyName, regionName, startDate, endDate);
            set({ isError: false, error: null, quotationsReportData: response });
        } catch (error) {
            set({ error: 'Failed to get quotation reports', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getAgencyLocalTravelData: async (intermediary: string, page?: number, pageSize?: number) => {
        set({ isLoading: true, filteredAgencyLocalTravelData: null });

        try {
            const response = await getAgencyLocalTravelData(intermediary, page, pageSize);
            set({ isError: false, error: null, agencyLocalTravelData: response });
        } catch (error) {
            set({ isError: true, error: 'Failed to get local travel data' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    addAgencyLocalTravelData: async (perksData: AddLocalTravelDataRequest, intermediary: string, page?: number, pageSize?: number) => {
        set({ isLoading: true });

        try {
            await addAgencyLocalTravelData(perksData);
            set({ isError: false, error: null });
            await get().getAgencyLocalTravelData(intermediary, page, pageSize);
        } catch (error) {
            set({ isError: true, error: 'Failed to add local travel data' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getAgencyBranches: async (agencyName: string) => {
        set({ isLoading: true });

        try {
            const response = await getAgencyBranches(agencyName);
            set({ isError: false, error: null, agencyBranches: response });
        } catch (error) {
            set({ isError: true, error: 'Failed to get agency branches.' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getFilteredAgencyLocalTravelData: async (
        agencyName: string,
        branch?: string,
        svNumber?: string,
        startDate?: string,
        endDate?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await getFilteredAgencyLocalTravelData(agencyName, branch, svNumber, startDate, endDate);
            set({ isError: false, error: null, filteredAgencyLocalTravelData: response });
        } catch (error) {
            set({ isError: true, error: 'Failed to get local travel data' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    updateAgencyLocalTravelData: async (perksData: AddLocalTravelDataRequest, intermediary: string, page?: number, pageSize?: number) => {
        set({ isLoading: true });

        try {
            await updateAgencyLocalTravelData(perksData);
            set({ isError: false, error: null });
            await get().getAgencyLocalTravelData(intermediary, page, pageSize);
        } catch (error) {
            set({ isError: true, error: 'Failed to update local travel data' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    cancelTravelSchedule: async (cancelRequest: CancelTravelScheduleRequest, intermediary: string, page?: number, pageSize?: number) => {
        set({ isLoading: true });

        try {
            await cancelTravelSchedule(cancelRequest);
            set({ isError: false, error: null });
            await get().getAgencyLocalTravelData(intermediary, page, pageSize);
        } catch (error) {
            set({ isError: true, error: 'Failed to cancel travel schedule' });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    }
}));
